import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Sparkles,
  Layout,
  ChevronRight,
  FileText,
  Palette,
  Zap,
  Upload,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import { axiosInstance } from "../lib/axios";
import { useProposalStore } from "../store/useProposalStore";
import { useAuthStore } from "../store/useAuthStore";
import ProposalBuilderEditor from "../components/proposals/builder/ProposalBuilderEditor";
import {
  TEMPLATE_CONFIGS,
  TEMPLATES,
  type TemplateType
} from "../config/templateConfig";
import { buildInfographicSections } from "../config/infographicTemplate";

const ProposalBuilderPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { createProposal, updateProposal, isCreatingProposal } = useProposalStore();
  const { authUser }: any = useAuthStore();

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateType | null>(null);
  const [jobDescription, setJobDescription] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedDraft, setGeneratedDraft] = useState<any>(null);
  // Documents / images attached at creation (JD PDF/DOCX, client files).
  const [attachments, setAttachments] = useState<any[]>([]);
  const [uploadingDocs, setUploadingDocs] = useState(false);

  // Proposal SOURCE — required; decides which domain the share link uses.
  // Upwork profile → that brand's subdomain · Website → chosen brand ·
  // Other → neutral omniworkspace.app.
  const [sourceType, setSourceType] = useState("");
  const [sourceAccount, setSourceAccount] = useState("");
  const [sourceTenantId, setSourceTenantId] = useState("");
  const [sourceOther, setSourceOther] = useState("");
  const [accounts, setAccounts] = useState<any[]>([]);
  const [tenants, setTenants] = useState<any[]>([]);
  // The client the proposal is for — it feeds the proposal with data the
  // system already holds instead of asking the BD to type it again.
  const [clientId, setClientId] = useState("");
  const [clients, setClients] = useState<any[]>([]);
  // Samples for the hero spotlight, so a new proposal opens with real work in
  // it rather than an empty right-hand column.
  const [portfolioAssets, setPortfolioAssets] = useState<any[]>([]);
  useEffect(() => {
    axiosInstance.get("/bids/accounts").then((r) => setAccounts(r.data || [])).catch(() => {});
    axiosInstance.get("/tenants").then((r) => setTenants(r.data?.tenants || [])).catch(() => {});
    axiosInstance.get("/clients").then((r) => setClients(r.data?.clients || r.data || [])).catch(() => {});
    axiosInstance
      .get("/proposals/portfolio-assets")
      .then((r) => setPortfolioAssets(r.data?.assets || r.data || []))
      .catch(() => {});
  }, []);
  const selectedClient = clients.find((c: any) => c._id === clientId) || null;

  // Everything the proposal can inherit rather than have re-typed.
  const clientInfoFromRecord = () => ({
    name: selectedClient?.name || "",
    company: selectedClient?.company || "",
    email: selectedClient?.email || "",
    phone: selectedClient?.phone || "",
    website: selectedClient?.website || "",
    note: "",
  });

  // Start the infographic template from its preset and pre-fill every section
  // the system can answer on the BD's behalf.
  const buildInfographicDraft = (aiDraft?: any) => {
    const info = clientInfoFromRecord();
    const sections = buildInfographicSections().map((s: any) => {
      const c = { ...s.content };
      let heading = s.title;
      let fromAi = false;

      const take = (value: any) => {
        fromAi = true;
        return value;
      };

      switch (s.type) {
        case "cover":
          if (aiDraft?.cover?.headline) {
            c.headline = take(aiDraft.cover.headline);
            c.subheadline = aiDraft.cover.subheadline || "";
            c.ledes = aiDraft.cover.ledes || [];
          } else {
            c.headline = aiDraft?.title || (jobDescription.split("\n")[0] || "").slice(0, 90);
          }
          break;

        case "client-info":
          Object.assign(c, info);
          break;

        case "understanding":
          if (aiDraft?.understanding?.text || aiDraft?.understanding?.heading) {
            heading = take(aiDraft.understanding.heading) || heading;
            c.text = aiDraft.understanding.text || "";
            c.points = aiDraft.understanding.points || [];
          }
          break;

        case "approach":
          if (aiDraft?.approach?.steps?.length) {
            heading = take(aiDraft.approach.heading) || heading;
            c.steps = aiDraft.approach.steps;
          }
          break;

        case "deliverables":
          if (aiDraft?.deliverables?.items?.length) {
            heading = take(aiDraft.deliverables.heading) || heading;
            c.items = aiDraft.deliverables.items;
          }
          break;

        case "pricing":
          if (aiDraft?.pricing?.tiers?.length) {
            take(null);
            c.currency = aiDraft.pricing.currency || "USD";
            c.note = aiDraft.pricing.note || "";
            c.tiers = aiDraft.pricing.tiers;
          }
          break;

        case "next-steps":
          if (aiDraft?.nextSteps?.text) {
            heading = take(aiDraft.nextSteps.heading) || heading;
            c.text = aiDraft.nextSteps.text;
          }
          // The ask matches where the proposal is going.
          c.buttonText = sourceType === "Upwork" ? "Message me on Upwork" : "Message me";
          break;

        case "portfolio":
          // Real samples from the Portfolio Library — never stock imagery.
          c.projects = (portfolioAssets || []).slice(0, 4).map((a: any) => ({
            title: String(a.name || "").replace(/\.[^/.]+$/, ""),
            meta: "",
            image: a.url || "",
          }));
          break;

        // "contact" is deliberately left empty: the page is signed by the
        // freelance profile the client already knows, not the BD's account.
        default:
          break;
      }

      return { ...s, title: heading, content: c, origin: fromAi ? "ai" : "template", approved: false };
    });

    return {
      title: aiDraft?.title || "New Proposal",
      category: aiDraft?.category || "general",
      template: "infographic",
      client: clientId || null,
      clientInfo: info,
      fullName: authUser?.fullName || "",
      tagline: "",
      profileImage: "",
      sections,
      attachments,
    };
  };
  const sourceReady =
    sourceType === "Upwork" ? !!sourceAccount :
    sourceType === "Website" ? !!sourceTenantId :
    sourceType === "Other" ? !!sourceOther.trim() : false;
  const sourcePayload = () => ({
    sourceType,
    upworkAccount: sourceType === "Upwork" ? sourceAccount : null,
    tenant: sourceType === "Website" ? sourceTenantId : null,
    sourceOther: sourceType === "Other" ? sourceOther.trim() : "",
  });

  const readAsDataUrl = (f: File) =>
    new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result));
      r.onerror = reject;
      r.readAsDataURL(f);
    });

  const uploadDocs = async (files: FileList | null) => {
    if (!files || !files.length) return;
    // Only PDF, DOCX, PNG, JPEG are accepted — anything else is rejected.
    const allowed = /\.(pdf|docx|png|jpe?g)$/i;
    const list = Array.from(files);
    list.filter((f) => !allowed.test(f.name)).forEach((f) => toast.error(`${f.name} — not supported. Only PDF, DOCX, PNG, JPEG.`));
    const ok = list.filter((f) => allowed.test(f.name));
    if (!ok.length) return;
    setUploadingDocs(true);
    try {
      const out: any[] = [];
      for (const f of ok) {
        const data = await readAsDataUrl(f);
        const { data: res } = await axiosInstance.post("/proposals/upload-doc", { data, fileName: f.name });
        out.push(res);
      }
      setAttachments((prev) => [...prev, ...out]);
    } catch {
      toast.error("Some files couldn't be uploaded.");
    } finally {
      setUploadingDocs(false);
    }
  };
  // Edit-with-builder: when a ?id= is present, load that saved proposal straight
  // into the editor (step 3) instead of the create wizard.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loadingExisting, setLoadingExisting] = useState(false);

  useEffect(() => {
    const id = searchParams.get("id");
    if (!id) return;
    setLoadingExisting(true);
    (async () => {
      try {
        const { data } = await axiosInstance.get(`/proposals/${id}`);
        setEditingId(id);
        setSelectedTemplate((data.template as TemplateType) || null);
        setAttachments(data.attachments || []);
        setGeneratedDraft(data);
        setStep(3);
      } catch (error) {
        console.error("Failed to load proposal for editing:", error);
        navigate("/proposals");
      } finally {
        setLoadingExisting(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

    const handleGenerate = async () => {
    if (!jobDescription.trim() || !selectedTemplate) return;
    if (!sourceReady) { toast.error("Select the Source first — Upwork profile, Website or Other."); return; }

    setIsGenerating(true);

    try {
      const res = await axiosInstance.post("/proposals/draft", {
        jobDescription,
        template: selectedTemplate,
        attachments, // client's attached PDFs/images → the AI reads them too
      });

      setGeneratedDraft(
        selectedTemplate === "infographic"
          ? buildInfographicDraft(res.data)
          : { ...res.data, template: selectedTemplate, attachments }
      );

      setStep(3);
    } catch (error: any) {
      console.error("Draft generation failed:", error);
      const msg = error?.response?.data?.message || error?.message || "The AI draft could not be generated.";
      if (selectedTemplate === "infographic") {
        toast.error(`${msg} — opening the proposal so you can write it yourself.`);
        setGeneratedDraft(buildInfographicDraft());
        setStep(3);
      } else {
        toast.error(msg);
      }
    } finally {
      setIsGenerating(false);
    }
  };

   const handleStartBlank = () => {
    if (!selectedTemplate) return;
    if (!sourceReady) { toast.error("Select the Source first — Upwork profile, Website or Other."); return; }
    if (selectedTemplate === "infographic") {
      setGeneratedDraft(buildInfographicDraft());
      setStep(3);
      return;
    }

    const templateConfig = TEMPLATE_CONFIGS[selectedTemplate];

    setGeneratedDraft({
      title: "New Proposal",
      category: "web design",
      template: selectedTemplate,
      fullName: "Irfan Baig",
      tagline: "",
      profileImage: "",
      stats: [
        { value: "100%", label: "JOB SUCCESS" },
        { value: "50+", label: "PROJECTS COMPLETED" },
        { value: "5★", label: "CLIENT RATING" },
        { value: "<4hrs", label: "RESPONSE TIME" },
      ],
      sections: templateConfig.defaultSections.map((id, index) => ({
        id,
        type: getDefaultSectionType(id),
        layout: "card",
        title: getDefaultSectionTitle(id),
        order: index + 1,
        content: selectedTemplate === "modern"
          ? getModernDefaultContent(id)
          : getDefaultSectionContent(id),
      })),
      attachments,
    });
    setStep(3);
  };

  const handleSave = async (finalData: any) => {
    try {
      const payload = {
        ...finalData,
        attachments: finalData.attachments || attachments,

        clientInfo: finalData.clientInfo || (clientId ? clientInfoFromRecord() : undefined),
        client: finalData.client ?? (clientId || null),
        ...(!editingId ? sourcePayload() : {}),
      };
      if (editingId) {
        await updateProposal(editingId, payload);
        navigate(`/proposals/${editingId}/review`);
        return;
      }
      // A new proposal goes straight to the review screen — that is where it is
      // validated and published, not from the list.
      const created = await createProposal(payload);
      navigate(created?._id ? `/proposals/${created._id}/review` : "/proposals");
    } catch (error) {
      console.error("Save failed:", error);
    }
  };

  if (loadingExisting) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#f8f9ff]">
        <div className="flex flex-col items-center gap-3 text-on-surface-variant">
          <div className="animate-spin h-8 w-8 border-[3px] border-primary border-t-transparent rounded-full" />
          <p className="text-sm font-semibold">Loading proposal in the builder…</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-[#f8f9ff] ${authUser?.role === "Business Developer" ? "" : "pt-16"}`}>

      {step === 1 && (
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              Choose Your Template
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto">
              Select a design template that matches your brand. You can customize colors and layout later.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {Object.values(TEMPLATE_CONFIGS).map((template) => (
              <div
                key={template.id}
                onClick={() => setSelectedTemplate(template.id)}
                className={`group relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 ${
                  selectedTemplate === template.id
                    ? "ring-4 ring-emerald-500 ring-offset-2 scale-[1.02]"
                    : "hover:shadow-xl hover:scale-[1.01]"
                }`}
              >

                <div
                  className="h-48 relative overflow-hidden"
                  style={{ backgroundColor: template.colors.background }}
                >

                  <div className="absolute inset-4 flex flex-col gap-2">

                    <div
                      className="h-8 rounded-lg flex items-center px-3 gap-2"
                      style={{ backgroundColor: template.colors.surface }}
                    >
                      <div
                        className="w-6 h-6 rounded-full"
                        style={{ backgroundColor: template.colors.primary }}
                      />
                      <div className="flex-1 h-2 rounded" style={{ backgroundColor: template.colors.muted + "30" }} />
                    </div>

                    <div className="flex-1 grid grid-cols-2 gap-2">
                      <div
                        className="rounded-lg p-2"
                        style={{ backgroundColor: template.colors.surface }}
                      >
                        <div className="h-2 w-3/4 rounded mb-2" style={{ backgroundColor: template.colors.primary }} />
                        <div className="h-1.5 rounded mb-1" style={{ backgroundColor: template.colors.muted + "30" }} />
                        <div className="h-1.5 w-2/3 rounded" style={{ backgroundColor: template.colors.muted + "30" }} />
                      </div>
                      <div
                        className="rounded-lg"
                        style={{ backgroundColor: template.colors.dark }}
                      />
                    </div>

                    <div
                      className="h-6 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: template.colors.primary }}
                    >
                      <div className="h-1.5 w-16 rounded bg-white/50" />
                    </div>
                  </div>

                  {selectedTemplate === template.id && (
                    <div className="absolute top-3 right-3 bg-emerald-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      Selected
                    </div>
                  )}
                </div>

                <div className="p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: template.colors.primary }}
                    />
                    <h3 className="font-bold text-gray-900">{template.name}</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">{template.description}</p>

                  <div className="flex gap-1">
                    {[template.colors.primary, template.colors.secondary, template.colors.accent, template.colors.dark].map((color, i) => (
                      <div
                        key={i}
                        className="w-6 h-6 rounded-full border-2 border-white shadow-sm"
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 flex justify-center">
            <button
              onClick={() => selectedTemplate && setStep(2)}
              disabled={!selectedTemplate}
              className={`flex items-center gap-3 px-8 py-4 rounded-xl font-semibold text-lg transition-all ${
                selectedTemplate
                  ? "bg-emerald-500 text-white hover:bg-emerald-600 shadow-lg shadow-emerald-500/30"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              Continue with {selectedTemplate ? TEMPLATE_CONFIGS[selectedTemplate].name : "Template"}
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="max-w-3xl mx-auto px-6 py-12">
          <div className="text-center mb-10">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              Generate with AI
            </h2>
            <p className="text-gray-600">
              Paste the job description and let AI create your proposal content
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg">
            {/* Source — REQUIRED. Decides which domain the client sees the proposal on. */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Source <span className="text-red-500">*</span>{" "}
                <span className="text-gray-400 font-normal">— which profile/website is this proposal for? (decides the share domain)</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {accounts.map((a: any) => (
                  <button
                    key={a._id}
                    type="button"
                    onClick={() => { setSourceType("Upwork"); setSourceAccount(a._id); }}
                    className={`px-3 py-2 rounded-xl border-2 text-sm font-medium transition ${sourceType === "Upwork" && sourceAccount === a._id ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}
                  >
                    Upwork — {a.name}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setSourceType("Website")}
                  className={`px-3 py-2 rounded-xl border-2 text-sm font-medium transition ${sourceType === "Website" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}
                >
                  Website
                </button>
                <button
                  type="button"
                  onClick={() => setSourceType("Other")}
                  className={`px-3 py-2 rounded-xl border-2 text-sm font-medium transition ${sourceType === "Other" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}
                >
                  Other
                </button>
              </div>
              {sourceType === "Website" && (
                <select
                  value={sourceTenantId}
                  onChange={(e) => setSourceTenantId(e.target.value)}
                  className="mt-2 w-full rounded-xl border-2 border-gray-200 px-4 py-2.5 text-sm text-gray-700 outline-none focus:border-emerald-500"
                >
                  <option value="">Which brand's website?</option>
                  {tenants.map((t: any) => <option key={t._id} value={t._id}>{t.name}</option>)}
                </select>
              )}
              {sourceType === "Other" && (
                <input
                  value={sourceOther}
                  onChange={(e) => setSourceOther(e.target.value)}
                  placeholder="Type the source (e.g. referral, LinkedIn…) — link will use omniworkspace.app"
                  className="mt-2 w-full rounded-xl border-2 border-gray-200 px-4 py-2.5 text-sm text-gray-700 outline-none focus:border-emerald-500"
                />
              )}
            </div>

            {/* Client — everything we already know about them is carried into
                the proposal instead of being typed a second time. */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Client <span className="text-gray-400 font-normal">— optional, pre-fills client information</span>
              </label>
              <select
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                className="w-full rounded-xl border-2 border-gray-200 px-4 py-2.5 text-sm text-gray-700 outline-none focus:border-emerald-500"
              >
                <option value="">Not linked to a client record</option>
                {clients.map((c: any) => (
                  <option key={c._id} value={c._id}>
                    {c.company ? `${c.company} — ${c.name}` : c.name}
                  </option>
                ))}
              </select>
            </div>

            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Job Description
            </label>
            <textarea
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              placeholder="Paste the Upwork job description here..."
              className="w-full h-64 rounded-xl border-2 border-gray-200 px-5 py-4 text-gray-700 placeholder:text-gray-400 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/20 outline-none transition-all resize-none"
            />

            {/* Attach documents & images (JD PDF/DOCX, client files) */}
            <div className="mt-5">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Attach documents &amp; images <span className="text-gray-400 font-normal">(JD PDF/DOCX, client files — optional)</span>
              </label>
              <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-emerald-400 text-gray-500 text-sm">
                <Upload size={16} /> {uploadingDocs ? "Uploading…" : "Click to upload — PDF, DOCX, PNG, JPEG only"}
                <input type="file" multiple accept=".pdf,.docx,.png,.jpg,.jpeg" className="hidden" onChange={(e) => uploadDocs(e.target.files)} />
              </label>
              {attachments.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {attachments.map((a, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 text-xs bg-gray-100 rounded-full pl-2.5 pr-1.5 py-1 text-gray-700">
                      {a.type?.startsWith("image/") ? "🖼" : "📄"} {a.name}
                      <button onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500"><X size={13} /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-4">
              <button
                onClick={handleGenerate}
                disabled={!jobDescription.trim() || isGenerating}
                className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-semibold transition-all ${
                  jobDescription.trim() && !isGenerating
                    ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:shadow-lg"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }`}
              >
                {isGenerating ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Generate with AI
                  </>
                )}
              </button>

              <button
                onClick={handleStartBlank}
                className="flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-semibold border-2 border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50 transition-all"
              >
                <FileText className="w-5 h-5" />
                Start Blank
              </button>
            </div>

            <button
              onClick={() => setStep(1)}
              className="mt-4 w-full text-center text-sm text-gray-500 hover:text-gray-700"
            >
              ← Back to template selection
            </button>
          </div>

          <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-5">
            <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Pro Tips
            </h4>
            <ul className="text-sm text-amber-700 space-y-1">
              <li>• Include the full job description for best results</li>
              <li>• AI will auto-detect category and generate relevant content</li>
              <li>• You can edit everything in the builder after generation</li>
            </ul>
          </div>
        </div>
      )}

      {step === 3 && generatedDraft && (
        <ProposalBuilderEditor
          initialData={generatedDraft}
          templateId={selectedTemplate!}
          onSave={handleSave}
          onBack={() => setStep(2)}
          isSaving={isCreatingProposal}
        />
      )}
    </div>
  );
};

const getDefaultSectionType = (id: string): string => {
  const typeMap: Record<string, string> = {
    overview: "simple-text",
    objectives: "feature-grid",
    challenges: "dark-grid",
    solution: "text-with-points",
    process: "steps",
    offer: "feature-grid",
    testimonials: "testimonial-grid",
    "recent-work": "portfolio-grid",
    cta: "call-to-action",
    footer: "footer",
    stats: "stats",
  };
  return typeMap[id] || "simple-text";
};

const getDefaultSectionTitle = (id: string): string => {
  const titleMap: Record<string, string> = {
    overview: "Project Overview",
    objectives: "Your Objectives",
    challenges: "Key Challenges",
    solution: "Our Creative Solution",
    process: "Our Approach",
    offer: "Our Offer",
    testimonials: "Testimonials",
    "recent-work": "Recent Work",
    cta: "Ready to Get Started?",
    footer: "Footer",
    stats: "Our Track Record",
  };
  return titleMap[id] || "New Section";
};

const getDefaultSectionContent = (id: string): any => {
  const contentMap: Record<string, any> = {
    overview: { text: "Add your project overview here..." },
    objectives: { items: [{ title: "Objective 1", description: "Description here" }] },
    challenges: { items: [{ title: "Challenge 1", description: "Description here" }] },
    solution: { text: "Our solution...", points: ["Point 1"], highlight: "Key benefit" },
    process: { steps: [{ title: "Step 1", description: "Description" }] },
    offer: { items: [{ title: "Deliverable 1", description: "Description" }] },
    testimonials: { items: [{ quote: "Great work!", name: "Client", position: "CEO" }] },
    "recent-work": { subtitle: "Selected projects", projects: [] },
    cta: { text: "Let's work together!", buttonText: "Hire Me Now" },
    footer: { text: "Professional Proposal" },
    stats: { items: [{ value: "100%", label: "Success Rate" }] },
  };
  return contentMap[id] || { text: "" };
};

const getModernDefaultContent = (id: string): any => {
  if (id === "overview") {
    return {
      text: "Add your professional story here...",
      storyHeadline: "I build things\nthat work.\nNot just things\nthat run.",
      storyRightPill: "Philosophy",
      pullquote: "Add a powerful quote about your professional philosophy",
      pullquoteAttr: "— Irfan Baig",
      timeline: [
        { icon: "💡", year: "The Beginning", title: "How it started", desc: "Your origin story..." },
        { icon: "🎨", year: "The Growth", title: "Mastering the craft", desc: "Your journey..." },
        { icon: "🚀", year: "Today", title: "Top Rated. 100% JSS.", desc: "Where you are now..." },
      ],
      credChips: [
        { icon: "⭐", text: "Top Rated" },
        { icon: "✅", text: "100% Job Success" },
        { icon: "🎨", text: "Your Skill" },
      ],
      storyBody: [
        "First paragraph about your approach...",
        "Second paragraph about your methodology...",
      ],
    };
  }
  if (id === "process") {
    return {
      steps: [
        { title: "Discovery", description: "Understanding your needs", icon: "🔍" },
        { title: "Design", description: "Creating the solution", icon: "✏️" },
        { title: "Development", description: "Building it right", icon: "⚙️" },
        { title: "Delivery", description: "Launching and handover", icon: "📦" },
      ],
      subtitle: "A structured approach that removes guesswork.",
    };
  }
  if (id === "offer") {
    return {
      items: [
        { title: "Deliverable 1", description: "Description" },
        { title: "Deliverable 2", description: "Description" },
        { title: "Deliverable 3", description: "Description" },
      ],
      tools: [
        { icon: "⚛️", name: "React" },
        { icon: "🐍", name: "Python" },
        { icon: "📐", name: "Figma" },
      ],
    };
  }

  return getDefaultSectionContent(id);
};

export default ProposalBuilderPage;
